import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add, EditOutlined, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { limitsText, NextManagerVersion } from '../components/NextManagerVersionSelector';
import api from '../services/api';
import { useApp } from '../contexts/AppContext';
import { softwareLicenseNumber } from '../utils/softwareLicenseNumber';

interface ManagerLicense {
  id: string; licenseNumber?: number; reference: string; product: string; edition: string; managerVersion: number;
  isPerpetual: boolean; expiresAt: string; status: string; company: { name: string }; managerEdition?: { maxUsers: number; maxMachines: number };
}
type Draft = { version: number; name: string; maxUsers: number; maxMachines: number };
const MAX_LIMIT = 100000;
const message = (e: any) => e.response?.data?.error?.message || 'Operazione non riuscita. Riprova.';
const statusLabels: Record<string, string> = { ACTIVE: 'Attiva', EXPIRED: 'Scaduta', SUSPENDED: 'Sospesa' };
export const draftErrors = (d: Draft): Partial<Record<keyof Draft, string>> => {
  const errors: Partial<Record<keyof Draft, string>> = {};
  if (!d.name.trim()) errors.name = 'Inserisci il nome della versione.';
  if (!Number.isInteger(d.maxUsers) || d.maxUsers < 1 || d.maxUsers > MAX_LIMIT) errors.maxUsers = `Inserisci un numero intero da 1 a ${MAX_LIMIT}.`;
  if (!Number.isInteger(d.maxMachines) || d.maxMachines < 1 || d.maxMachines > MAX_LIMIT) errors.maxMachines = `Inserisci un numero intero da 1 a ${MAX_LIMIT}.`;
  return errors;
};

/** Section of the license dashboard dedicated to NEXT Manager: the ten versions and the licenses that use them. */
export default function NextManagerLicensesPage() {
  const navigate = useNavigate();
  const { state } = useApp();
  const isAdmin = state.auth.user?.role === 'administrator';
  const [versions, setVersions] = useState<NextManagerVersion[]>([]);
  const [licenses, setLicenses] = useState<ManagerLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dialogError, setDialogError] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [v, l] = await Promise.all([api.get('/software-licenses/next-manager/versions'), api.get('/software-licenses')]);
      setVersions(v.data.data.versions);
      setLicenses(l.data.data.licenses.filter((x: ManagerLicense) => x.product === 'NextManager'));
    } catch (e) { setError(message(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const edit = (v: NextManagerVersion) => { setDraft({ version: v.version, name: v.name, maxUsers: v.maxUsers, maxMachines: v.maxMachines }); setErrors({}); setDialogError(''); };
  const save = async () => {
    if (!draft) return;
    const found = draftErrors(draft); setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true); setDialogError('');
    try {
      await api.put(`/software-licenses/next-manager/versions/${draft.version}`, { name: draft.name.trim(), maxUsers: draft.maxUsers, maxMachines: draft.maxMachines });
      setNotice(`Versione ${draft.version} aggiornata: i server la ricevono al prossimo rinnovo del certificato.`);
      setDraft(null);
      await load();
    } catch (e) { setDialogError(message(e)); }
    finally { setSaving(false); }
  };
  return <Stack spacing={3}>
    <PageHeader title="NEXT Manager" subtitle="Le 10 versioni di NEXT Manager, con gli utenti e le macchine che ognuna comprende, e le licenze dei clienti."
      actions={<>
        <Button startIcon={<Refresh />} onClick={() => void load()} disabled={loading}>Aggiorna</Button>
        <Button startIcon={<Add />} variant="contained" onClick={() => navigate('/licenses?product=NextManager')}>Nuova licenza</Button>
      </>} />
    {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
    <Paper component="section" aria-label="Versioni di NEXT Manager" sx={{ overflow: 'hidden', boxShadow: 'none' }}>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography component="h2" fontWeight={700}>Versioni</Typography>
        <Typography variant="body2" color="text.secondary">Ogni licenza sceglie una versione. Una riduzione dei limiti non cancella utenti o macchine già presenti: il Manager non ne fa aggiungere altri.</Typography>
      </Box>
      {loading ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress aria-label="Caricamento versioni" /></Box> : <TableContainer>
        <Table sx={{ minWidth: 640 }}>
          <TableHead><TableRow>{['Versione', 'Nome', 'Utenti massimi', 'Macchine massime', 'Moduli', 'Licenze', ...(isAdmin ? [''] : [])].map(h => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {versions.map(v => <TableRow key={v.version}>
              <TableCell>{v.version}</TableCell>
              <TableCell><Typography variant="body2" fontWeight={700}>{v.name}</Typography></TableCell>
              <TableCell>{v.maxUsers}</TableCell>
              <TableCell>{v.maxMachines}</TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{v.modules?.length ? v.modules.join(', ') : 'Da definire'}</Typography></TableCell>
              <TableCell>{v.licenseCount}</TableCell>
              {isAdmin && <TableCell align="right"><Button startIcon={<EditOutlined />} onClick={() => edit(v)} aria-label={`Modifica versione ${v.version}`}>Modifica</Button></TableCell>}
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>}
    </Paper>
    <Paper component="section" aria-label="Licenze NEXT Manager" sx={{ overflow: 'hidden', boxShadow: 'none' }}>
      <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography component="h2" fontWeight={700}>Licenze dei clienti</Typography>
        <Button onClick={() => navigate('/licenses?product=NextManager')}>Gestisci in Licenze software</Button>
      </Box>
      {!loading && <TableContainer>
        <Table sx={{ minWidth: 640 }}>
          <TableHead><TableRow>{['Cliente / riferimento', 'Versione', 'Limiti', 'Diritto d’uso'].map(h => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {licenses.map(l => <TableRow key={l.id}>
              <TableCell>
                <Typography variant="body2" fontWeight={700}>{l.company.name}</Typography>
                <Typography variant="body2" color="primary.main" fontWeight={700}>{softwareLicenseNumber(l.licenseNumber)}</Typography>
                <Typography variant="caption" color="text.secondary">{l.reference}</Typography>
              </TableCell>
              <TableCell>{l.managerVersion} · {l.edition}</TableCell>
              <TableCell>{l.managerEdition ? limitsText(l.managerEdition) : '—'}</TableCell>
              <TableCell>{statusLabels[l.status] || l.status} · {l.isPerpetual ? 'Perpetua' : new Date(l.expiresAt).toLocaleDateString('it-IT', { timeZone: 'UTC' })}</TableCell>
            </TableRow>)}
            {!licenses.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>
              <Typography fontWeight={600}>Nessuna licenza NEXT Manager.</Typography>
              <Typography variant="body2" color="text.secondary">Crea una licenza per assegnare il Manager a un cliente.</Typography>
            </TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>}
    </Paper>
    <Dialog open={!!draft} fullWidth maxWidth="xs" onClose={() => { if (!saving) setDraft(null); }} aria-labelledby="manager-version-title">
      <DialogTitle id="manager-version-title">Versione {draft?.version}</DialogTitle>
      {draft && <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        {dialogError && <Alert severity="error">{dialogError}</Alert>}
        <TextField label="Nome" required value={draft.name} disabled={saving} inputProps={{ maxLength: 100 }} error={!!errors.name} helperText={errors.name || 'Il nome compare nella licenza e nel Manager del cliente.'} onChange={e => setDraft({ ...draft, name: e.target.value })} />
        <TextField label="Utenti massimi" type="number" required value={draft.maxUsers} disabled={saving} inputProps={{ min: 1, max: MAX_LIMIT }} error={!!errors.maxUsers} helperText={errors.maxUsers || 'Utenti attivi che il Manager consente.'} onChange={e => setDraft({ ...draft, maxUsers: Number(e.target.value) })} />
        <TextField label="Macchine massime" type="number" required value={draft.maxMachines} disabled={saving} inputProps={{ min: 1, max: MAX_LIMIT }} error={!!errors.maxMachines} helperText={errors.maxMachines || 'Macchine che il Manager consente di registrare.'} onChange={e => setDraft({ ...draft, maxMachines: Number(e.target.value) })} />
        <Alert severity="info">I server ricevono i nuovi limiti al prossimo rinnovo del certificato, di solito entro un minuto se sono collegati.</Alert>
      </Stack></DialogContent>}
      <DialogActions>
        <Button disabled={saving} onClick={() => setDraft(null)}>Annulla</Button>
        <Button variant="contained" disabled={saving} onClick={() => void save()}>{saving ? 'Salvataggio…' : 'Salva'}</Button>
      </DialogActions>
    </Dialog>
  </Stack>;
}
